import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './selectors.css';

interface DropdownProps {
  open: boolean;
  /** The element the menu anchors to (typically the pill that opened it). */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Menu width in px. */
  width?: number;
  children: React.ReactNode;
}

/**
 * Anchored popover rendered in a fixed layer below its anchor, right-aligned to
 * it. Closes on outside-click, Escape, scroll, or resize. Mirrors the mobile
 * pairPanel/tfPanel styling.
 */
export function Dropdown({ open, anchor, onClose, width = 200, children }: DropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor) {
      setPos(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    // Right-align the menu to the anchor, clamped to the viewport.
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPos({ top: rect.bottom + 6, left });
  }, [open, anchor, width]);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return; // clicking the anchor toggles separately
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Close on scroll/resize since the absolute position would otherwise drift —
    // but not when the scroll happens inside the menu itself (the capture-phase
    // listener sees those too, and a long list has to be scrollable).
    const onScrollOrResize = (e?: Event) => {
      if (e?.type === 'scroll' && e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      onClose();
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, anchor, onClose]);

  if (!open || !pos) return null;

  return (
    <div
      ref={menuRef}
      className="dropdown-menu"
      style={{ top: pos.top, left: pos.left, width }}
      role="listbox"
    >
      {children}
    </div>
  );
}
